import AjustableImage from 'Custom/AjustableImage';
import HorizontalCard from 'Custom/Card/HorizontalCard';
import Duration from 'Custom/Duration';
import { Finder } from 'Custom/Finder';
import HeroQuestion from 'Custom/HeroQuestion/index';
import InsertionTiming from 'Custom/InsertionTiming';
import ItemBreakpoint from 'Custom/ItemBreakpoint/index';
import RichText from 'Custom/RichText';
import SplitContent from 'Custom/Split/SplitContent';
import { AlternativeTable } from 'Custom/Table/AlternativeTable';
import HERO_BG_IMAGE_MOBILE from 'Images/background/blue-bg-mobile.png';
import HERO_BG_IMAGE from 'Images/background/dark-blue-desktop.png';
import ArrowRight from 'Images/brand-icons/arrow-md-right.svg';
import ARM_IMAGE_DESKTOP from 'Images/misc/arm-split-desktop.webp';
import BANDAGE_IMAGE_DESKTOP from 'Images/misc/bandage-desktop.webp';
import CALENDAR_IMAGE_DESKTOP from 'Images/misc/calendar-with-circle-desktop.webp';
import CALENDAR_IMAGE_MOBILE from 'Images/misc/calendar-with-circle-mobile.webp';
import Link from 'next/link';
import Button from 'Nori/Button';
import Container from 'Nori/Container';
import Hero from 'Nori/Hero/HeroFull2';
import Layout from 'Nori/Layout';
import styles from 'styles/pages/insertionAftercare.module.scss';
import { SE_TABLE_3 } from 'Utils/side-effects';

const HEAD = {
  title:
    'Insertion and Aftercare for NEXPLANON® (etonogestrel implant) 68 mg Radiopaque',
  description:
    'See how NEXPLANON® (etonogestrel implant) 68 mg Radiopaque is inserted and learn about aftercare following the in-office insertion procedure.',
  keywords:
    'Nexplanon Insertion, Nexplanon Insertion Procedure, How is Nexplanon Inserted',
};

export default function InsertionAftercare() {
  return (
    <Layout head={HEAD}>
      <Hero
        image={HERO_BG_IMAGE}
        mobileImage={HERO_BG_IMAGE_MOBILE}
        className={styles.heroInsertionAftercare}
        data-qa-tag="heror insertion and aftercare"
      >
        <Container>
          <h4 className={styles.heroBaseHeading} data-qa-tag="heroBaseHeading">
            INSERTION & AFTERCARE
          </h4>
          <div className={styles.heroHeading}>
            <div className={styles.middleHeroText} data-qa-tag="middleHeroText">
              discuss <span>Nexplanon</span> with your doctor <br />
              to see if it&rsquo;s right for you
            </div>
          </div>
          <div className={styles.heroSubheading} data-qa-tag="heroSubheading">
            It takes a few minutes<sup>#</sup> in a doctor&rsquo;s office to
            have NEXPLANON inserted in your arm.
          </div>
          <div className={styles.heroInfoPara}>
            <sup>#</sup>In a clinical trial, the mean insertion time (from the
            removal of the protection cap of the applicator until retraction of
            the needle from the arm) was 27.9 ± 29.3 seconds.
          </div>
        </Container>
      </Hero>

      <SplitContent
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="where is NEXPLANON inserted?"
        data-design-module_no="02"
        className={styles.sectionWhereIsNxInserted}
        data-qa-tag="where is NEXPLANON inserted?"
      >
        <ItemBreakpoint breakpoint="lg">
          <AjustableImage
            size="full"
            image={{ src: ARM_IMAGE_DESKTOP, style: { width: 292 }, alt: '' }}
          />
          <AjustableImage
            size="full"
            image={{ src: ARM_IMAGE_DESKTOP, style: { width: 146 }, alt: '' }}
          />
        </ItemBreakpoint>
        <>
          <RichText data-qa-tag="richtext Where is NEXPLANON inserted?">
            <h2 data-qa-tag="title Where is NEXPLANON inserted?">
              Where is NEXPLANON inserted?
            </h2>
            <p data-qa-tag="Where is NEXPLANON inserted a">
              You probably know NEXPLANON is inserted in your arm, but where
              might still be a mystery.
            </p>
            <p data-qa-tag="Where is NEXPLANON inserted b">
              A trained healthcare professional will insert the NEXPLANON
              implant on the inside of your upper, non-dominant arm.
            </p>
            <p data-qa-tag="Where is NEXPLANON inserted c">
              The implant is placed discreetly underneath the skin. While you
              won't necessarily see it, you should be able to feel it just
              beneath the skin if you touch it.
            </p>
            <p data-qa-tag="Where is NEXPLANON inserted d">
              A trained healthcare professional will cover the site where
              NEXPLANON was placed with 2 bandages. Leave the top bandage on for
              24 hours. Keep the bottom bandage clean, dry, and in place for 3
              to 5 days.
            </p>
          </RichText>
        </>
      </SplitContent>

      <SplitContent
        reverse
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="the timing of the insertion is important"
        data-design-module_no="03"
        className={styles.sectionTimingOfInsertion}
        data-qa-tag="The timing of the insertion is important"
      >
        <ItemBreakpoint breakpoint="lg">
          <AjustableImage
            size="full"
            image={{
              src: CALENDAR_IMAGE_DESKTOP,
              style: { width: 292 },
              alt: '',
            }}
          />
          <AjustableImage
            size="full"
            image={{
              src: CALENDAR_IMAGE_MOBILE,
              style: { width: 146 },
              alt: '',
            }}
          />
        </ItemBreakpoint>
        <>
          <RichText data-qa-tag="richtext The timing of the insertion is important">
            <h2 data-qa-tag="title The timing of the insertion is important">
              The timing of the insertion is important
            </h2>
            <p data-qa-tag="The timing of the insertion a">
              A trained healthcare professional may perform a pregnancy test
              before inserting NEXPLANON.
            </p>
            <p data-qa-tag="The timing of the insertion b">
              A trained healthcare professional will help you determine when to
              have{' '}
              <Link
                href="#timing-of-insertion"
                data-design-type="clickAction"
                data-design-category="content_link_vd"
                data-design-action="Link-Anchor"
                data-design-label="NEXPLANON inserted"
                data-design-nori_id="Inline Content"
                data-design-module_name="the timing of the insertion is important"
                data-design-module_no="03"
                data-qa-tag="link nexplanon inserted"
              >
                NEXPLANON inserted
              </Link>
              . The timing will depend upon whether you are currently using
              birth control and which method you are using. You should not use
              NEXPLANON if you are pregnant or think you may be pregnant.
            </p>
            <p data-qa-tag="The timing of the insertion c">
              You should have NEXPLANON inserted within 5 days of your regular
              menstrual bleeding. If NEXPLANON is inserted after the fifth day
              of your period, then you should use additional contraceptive
              methods, such as a condom, for the first 7 days after insertion.
            </p>
          </RichText>
        </>
      </SplitContent>

      <SplitContent
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="following insertion, you will wear a pressure bandage for 24 hours"
        data-design-module_no="04"
        className={styles.sectionBandageAfterInsertion}
        data-qa-tag="Following insertion, you will wear a pressure bandage for 24 hours"
      >
        <ItemBreakpoint breakpoint="lg">
          <AjustableImage
            size="full"
            image={{
              src: BANDAGE_IMAGE_DESKTOP,
              style: { width: 292 },
              alt: '',
            }}
          />
          <AjustableImage
            size="full"
            image={{
              src: BANDAGE_IMAGE_DESKTOP,
              style: { width: 146 },
              alt: '',
            }}
          />
        </ItemBreakpoint>
        <>
          <RichText data-qa-tag="richtext Following insertion, you will wear a pressure bandage for 24 hours">
            <h2 data-qa-tag="title Following insertion, you will wear a pressure bandage for 24 hours">
              Following insertion, you will wear a pressure bandage for 24 hours
            </h2>
            <p data-qa-tag="Following insertion a">
              After 24 hours, you'll want to remove the pressure bandage and
              keep the adhesive bandage on for 3-5 days until the insertion site
              fully heals.
            </p>
            <Button
              type="simple"
              href="#insertion-aftercare"
              data-design-type="clickAction"
              data-design-category="content_link_vd"
              data-design-action="Link-Anchor"
              data-design-label="Details on after-insertion care"
              data-qa-tag="Details on after-insertion care"
              data-design-nori_id="Inline Content"
              data-design-module_name="following insertion, you will wear a pressure bandage for 24 hours"
              data-design-module_no="04"
            >
              Details on after-insertion care <ArrowRight />
            </Button>
          </RichText>
        </>
      </SplitContent>

      <HorizontalCard
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="Already on nexplanon"
        data-design-module_no="05"
        data-qa-tag="section already on nexplanon"
      />

      <Duration
        title="Insertion Aftercare"
        anchorID="insertion-aftercare"
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="insertion aftercare"
        data-design-module_no="06"
        data-qa-tag="section You just got NEXPLANON"
      >
        <h2 data-qa-tag="title You just got NEXPLANON">
          You just got NEXPLANON
        </h2>
        <p className="intro" data-qa-tag="intro You just got NEXPLANON">
          Here is some helpful information to keep you informed. And if you have
          any questions, please consult your doctor.
        </p>
      </Duration>

      <InsertionTiming
        id="timing-of-insertion"
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="the timing of your insertion"
        data-design-module_no="07"
        data-qa-tag="wrapper the timing of your insertion"
      />

      <div
        className={styles.tableBg}
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="call your doctor immediately if you have …"
        data-design-module_no="08"
        data-qa-tag="section When should I call my healthcare professional?"
      >
        <Container>
          <RichText
            className={styles.tableWrapper}
            data-qa-tag="richtext When should I call my healthcare professional?"
          >
            <div className={styles.title}>
              <h2 data-qa-tag="title When should I call my healthcare professional?">
                When should I call my healthcare professional?
              </h2>
              <div
                className="intro"
                data-qa-tag="intro When should I call my healthcare professional?"
              >
                Call your healthcare professional immediately if you have…
              </div>
            </div>
            <AlternativeTable
              content={SE_TABLE_3}
              size="sm"
              title="call your doctor immediately if you have …"
              data-qa-tag="insertion table"
            />
          </RichText>
        </Container>
      </div>
      <HeroQuestion></HeroQuestion>
      <Finder data-design-module_no="10" />
    </Layout>
  );
}
